import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Phone } from 'lucide-react';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const validatePhone = (phoneNumber) => {
    // Remove spaces and dashes
    const cleaned = phoneNumber.replace(/[\s-]/g, '');
    // Check if it starts with + and has 7-15 digits
    const pattern = /^\+\d{7,15}$/;
    return pattern.test(cleaned);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validatePhone(phone)) {
      setError('Phone must be in international format with country code (e.g., +919876543210, +14155552671)');
      return;
    }

    setLoading(true);
    // Passwordless registration - no password needed!
    const result = await register(name, email, phone.replace(/[\s-]/g, ''));
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#1E3A5F] mb-2">Create Account</h1>
          <p className="text-gray-600">Join the CSED Digital Knowledge Platform</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="John Doe"
            />
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="john@example.com"
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone Number *</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder="+911234567890"
                className="pl-10"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Include country code (e.g., +91 for India, +1 for US)
            </p>
          </div>

          {/* OTP Login Info */}
          <div className="p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-xs text-gray-700">
              <span className="font-semibold">🔐 Passwordless Login:</span> You'll login using OTP (One-Time Password) sent to your phone or email. No password needed!
            </p>
          </div>

          {/* Privacy Note */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-xs text-gray-700">
              <span className="font-semibold">Privacy Notice:</span> Your phone number will be used for OTP delivery, event updates, and important notifications.
            </p>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#E87722] hover:bg-[#d66a1a] text-white"
          >
            {loading ? 'Creating Account...' : 'Register'}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="text-[#E87722] hover:underline font-medium">
            Login here
          </Link>
        </div>

        <div className="mt-4 text-center">
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">
            ← Back to Home
          </Link>
        </div>
      </Card>
    </div>
  );
}
